/**
 * WebSocket client for knowledge-base training on the duplex transport.
 *
 * Same framing as Playbook Live (`playbookLiveWebSocketService`), on the
 * training service's own URL (`config.server.trainingUrl`, port 6340):
 *   text frames   -> JSON control events, both directions
 *   binary frames -> raw PCM16 mono 24 kHz, both directions
 *
 * The first frame is always `start_session`. After that the page streams the
 * microphone continuously, silence included; the voice model produces
 * nothing without an inbound stream.
 *
 * No auto-reconnect: the voice session cannot be resumed, so a silent
 * reconnect would hand the rep a customer with no memory of the call.
 */
import { getAuth } from 'firebase/auth';

import { config } from '../config';
import {
  CompetitorCardData,
  ItemAnsweredData,
  ItemRaisedData,
  LandmineCardData,
  QuestionDetectedData,
  SessionCompletedData,
  SessionStartedData,
  SilenceData,
  StoredAnswerData,
  TranscriptDeltaData,
  parseCompetitorCard,
  parseItemAnswered,
  parseItemRaised,
  parseLandmineCard,
  parseQuestionDetected,
  parseSessionCompleted,
  parseSessionStarted,
  parseSilence,
  parseStoredAnswer,
  parseTranscriptDelta,
  toText,
} from './kbLiveFrames';

export const KB_LIVE_ERRORS = {
  notSignedIn: 'You need to be signed in to start a training session.',
  couldNotConnect: 'We could not reach the training service. Please try again.',
  disconnected: 'The training session was disconnected. Please start it again.',
  generic: 'Something went wrong with the training session.',
} as const;

export interface KbLiveHandlers {
  onSessionStarted?: (data: SessionStartedData) => void;
  onTranscriptDelta?: (data: TranscriptDeltaData) => void;
  onItemRaised?: (data: ItemRaisedData) => void;
  onQuestionDetected?: (data: QuestionDetectedData) => void;
  onStoredAnswer?: (data: StoredAnswerData) => void;
  onLandmineAlert?: (data: LandmineCardData) => void;
  onCompetitorAlert?: (data: CompetitorCardData) => void;
  onItemAnswered?: (data: ItemAnsweredData) => void;
  onSilence?: (data: SilenceData) => void;
  onSessionCompleted?: (data: SessionCompletedData) => void;
  onCustomerAudio?: (pcm: ArrayBuffer) => void;
  onError?: (message: string) => void;
}

/** Calls `handler` with the parsed frame, or not at all when it is unusable. */
const deliver = <T>(parsed: T | null, handler?: (data: T) => void): void => {
  if (parsed) handler?.(parsed);
};

export class KbLiveWebSocketService {
  private socket: WebSocket | null = null;
  private handlers: KbLiveHandlers = {};
  /** True once the socket close is expected: a manual disconnect, or the
   *  server closing after `session_completed` / `error`. */
  private isCloseExpected = false;

  setHandlers(handlers: KbLiveHandlers): void {
    this.handlers = handlers;
  }

  get isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  async connect(trackId: string, sessionId?: string): Promise<void> {
    this.isCloseExpected = false;
    const token = await getAuth().currentUser?.getIdToken();
    if (!token) throw new Error(KB_LIVE_ERRORS.notSignedIn);

    return new Promise<void>((resolve, reject) => {
      const socket = new WebSocket(config.server.trainingUrl);
      // Customer audio is binary; without this every frame arrives as a Blob.
      socket.binaryType = 'arraybuffer';
      this.socket = socket;
      let isOpen = false;

      socket.onopen = () => {
        isOpen = true;
        socket.send(JSON.stringify({ type: 'start_session', data: { token, trackId, sessionId } }));
        resolve();
      };
      socket.onmessage = (event: MessageEvent) => this.handleMessage(event);
      socket.onerror = () => {
        if (!isOpen) reject(new Error(KB_LIVE_ERRORS.couldNotConnect));
      };
      socket.onclose = () => {
        if (this.socket === socket) this.socket = null;
        if (!isOpen) {
          reject(new Error(KB_LIVE_ERRORS.couldNotConnect));
          return;
        }
        if (!this.isCloseExpected) this.handlers.onError?.(KB_LIVE_ERRORS.disconnected);
      };
    });
  }

  /** Forward one microphone frame. Fire-and-forget by design. */
  sendAudio(pcm: ArrayBuffer): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(pcm);
  }

  /** Ask the server to wrap up; it answers with `session_completed`. */
  endSession(): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type: 'end_session' }));
    }
  }

  disconnect(): void {
    this.isCloseExpected = true;
    this.endSession();
    this.socket?.close();
    this.socket = null;
  }

  private handleMessage(event: MessageEvent): void {
    if (event.data instanceof ArrayBuffer) {
      this.handlers.onCustomerAudio?.(event.data);
      return;
    }
    let message: { type?: unknown; data?: unknown } | null = null;
    try {
      message = JSON.parse(String(event.data));
    } catch (parseError) {
      // One malformed frame must not tear down a live session.
      console.warn('KbLive: dropped an unparseable server frame', parseError);
      return;
    }
    this.route(toText(message?.type), message?.data ?? {});
  }

  private route(type: string, data: unknown): void {
    const handlers = this.handlers;
    switch (type) {
      case 'session_started':
        handlers.onSessionStarted?.(parseSessionStarted(data));
        return;
      case 'transcript_delta':
        deliver(parseTranscriptDelta(data), handlers.onTranscriptDelta);
        return;
      case 'item_raised':
        deliver(parseItemRaised(data), handlers.onItemRaised);
        return;
      case 'question_detected':
        handlers.onQuestionDetected?.(parseQuestionDetected(data));
        return;
      case 'stored_answer_generated':
        handlers.onStoredAnswer?.(parseStoredAnswer(data));
        return;
      case 'landmine_alert':
        handlers.onLandmineAlert?.(parseLandmineCard(data));
        return;
      case 'competitor_alert':
        handlers.onCompetitorAlert?.(parseCompetitorCard(data));
        return;
      case 'item_answered':
        deliver(parseItemAnswered(data), handlers.onItemAnswered);
        return;
      case 'silence':
        deliver(parseSilence(data), handlers.onSilence);
        return;
      case 'session_completed':
        // The server closes the socket right after this; that close is normal.
        this.isCloseExpected = true;
        handlers.onSessionCompleted?.(parseSessionCompleted(data));
        return;
      case 'error':
        // An error frame is followed by a close; report the reason, not the close.
        this.isCloseExpected = true;
        handlers.onError?.(toText((data as { message?: unknown })?.message) || KB_LIVE_ERRORS.generic);
        return;
      default:
        return;
    }
  }
}

export const kbLiveWebSocketService = new KbLiveWebSocketService();
